import React, { useState, useEffect } from 'react';
import { Plus, Rocket, Users, Database, Lightbulb, Calendar, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Spinoff, Staff, Project, IPEntity } from '../types';
import { SpinoffModal } from '../components/SpinoffModal';

export function Spinoffs() {
  const [spinoffs, setSpinoffs] = useState<Spinoff[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allIP, setAllIP] = useState<IPEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewSpinoff, setShowNewSpinoff] = useState(false);
  const [selectedSpinoff, setSelectedSpinoff] = useState<Spinoff | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [spinoffsResponse, staffResponse, projectsResponse, ipResponse] = await Promise.all([
        supabase
          .from('spinoffs')
          .select(`
            *,
            ip:spinoff_ip (
              ip:ip_entities (
                id,
                title,
                descriptions:ip_descriptions(
                  id,
                  content,
                  display_order
                ),
                images:ip_images(
                  id,
                  url,
                  display_order
                ),
                links:ip_links(
                  id,
                  url,
                  title
                ),
                notes:ip_notes(
                  id,
                  content
                ),
                tags:ip_tags (
                  tag:tags (
                    id,
                    name
                  )
                ),
                staff:ip_staff (
                  staff:staff (
                    id,
                    name,
                    role,
                    experience_level
                  )
                )
              )
            ),
            projects:spinoff_projects (
              project:projects (
                id,
                project_name,
                description,
                budget,
                customer:customers (
                  id,
                  name
                )
              )
            ),
            staff:spinoff_staff (
              staff:staff (
                id,
                name,
                role,
                experience_level
              )
            )
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('staff')
          .select('*')
          .order('name'),
        supabase
          .from('projects')
          .select('*, customer:customers(id, name)')
          .order('project_name'),
        supabase
          .from('ip_entities')
          .select('*')
          .order('title')
      ]);

      if (spinoffsResponse.error) throw spinoffsResponse.error;
      if (staffResponse.error) throw staffResponse.error;
      if (projectsResponse.error) throw projectsResponse.error;
      if (ipResponse.error) throw ipResponse.error;

      setSpinoffs(spinoffsResponse.data.map((spinoff: any) => ({
        ...spinoff,
        ip: spinoff.ip.map((i: any) => i.ip),
        projects: spinoff.projects.map((p: any) => p.project),
        staff: spinoff.staff.map((s: any) => s.staff)
      })));
      setAllStaff(staffResponse.data);
      setAllProjects(projectsResponse.data);
      setAllIP(ipResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalBudget = (projects: Project[]): string => {
    const total = projects.reduce((sum, project) => {
      if (!project.budget) return sum;
      const value = parseFloat(project.budget.replace(/[^0-9.-]+/g, ''));
      return isNaN(value) ? sum : sum + value;
    }, 0);

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(total);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading spin-offs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Rocket className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Spin-offs</h1>
          </div>
          <button
            onClick={() => setShowNewSpinoff(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Spin-off
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spinoffs.map((spinoff) => (
            <div
              key={spinoff.id}
              onClick={() => setSelectedSpinoff(spinoff)}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {spinoff.name}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {spinoff.description}
                </p>
                <div className="space-y-3">
                  {spinoff.year && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Started {spinoff.year}</span>
                    </div>
                  )}
                  {spinoff.revenue && (
                    <div className="flex items-center text-sm text-gray-500">
                      <DollarSign className="w-4 h-4 mr-2" />
                      <span>{spinoff.revenue}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    <span>{spinoff.ip.length} IP Assets</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Database className="w-4 h-4 mr-2" />
                    <span>
                      {spinoff.projects.length} Projects • {calculateTotalBudget(spinoff.projects)}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{spinoff.staff.length} Team Members</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {(showNewSpinoff || selectedSpinoff) && (
          <SpinoffModal
            spinoff={selectedSpinoff || undefined}
            allStaff={allStaff}
            allProjects={allProjects}
            allIP={allIP}
            onClose={() => {
              setShowNewSpinoff(false);
              setSelectedSpinoff(null);
            }}
            onSave={() => {
              loadData();
              setShowNewSpinoff(false);
              setSelectedSpinoff(null);
            }}
            isNew={!selectedSpinoff}
          />
        )}
      </div>
    </div>
  );
}